import clsx from 'clsx'
import { formatDate } from 'date-fns'
import { DayContentProps } from 'react-day-picker'

export interface UseDayContentProps {
  /**
   * Allows fully-customized control on the formatted day content
   * If this function returns false, default DayContent formatting will take effect
   */
  customFormat?: (
    props: DayContentProps,
  ) => ReturnType<typeof DayContent> | null
}

export const useDayContent = ({ customFormat }: UseDayContentProps) =>
  function CustomDayContent(props: DayContentProps) {
    const CustomDayFormat = customFormat && customFormat(props)

    if (CustomDayFormat) {
      return CustomDayFormat
    }

    return <DayContent {...props} />
  }

/** Render the content of the day cell. */
export default function DayContent({ date, activeModifiers }: DayContentProps) {
  const textTypeClassName =
    activeModifiers.today || activeModifiers.selected
      ? 'type-body-9'
      : 'type-body-10'
  const textColorClassName =
    activeModifiers.outside || activeModifiers.disabled
      ? 'text-neutral-500'
      : activeModifiers.today
        ? 'text-theme-accessibility'
        : 'text-neutral-700'
  const hoverClassNames = [
    !activeModifiers.disabled && 'hover:border',
    activeModifiers.selected
      ? 'hover:border-theme-secondary'
      : 'hover:border-current',
  ]
  const focusClassNames = !activeModifiers.disabled && [
    'focus-visible:border focus-visible:border-current focus-visible:outline-none',
    'focus:border focus:border-current focus:outline-none',
  ]
  const disabledClassNames =
    activeModifiers.disabled &&
    'cursor-not-allowed [text-decoration:line-through]'

  return (
    <div
      data-disabled={activeModifiers?.disabled}
      aria-label={formatDate(date, 'MMMM d, yyyy')}
      className={clsx(
        'flex items-center justify-center w-6 h-6 rounded-8 mx-auto',
        textTypeClassName,
        textColorClassName,
        hoverClassNames,
        focusClassNames,
        disabledClassNames,
        activeModifiers.selected &&
          'border border-theme-secondary bg-theme-accent-1',
      )}
    >
      {formatDate(date, 'd')}
    </div>
  )
}
